import { Box, Typography, Popover, useTheme, Button, PopoverOrigin } from '@mui/material';
import { OpenInBrowserOutlined } from '@mui/icons-material';
import { FindManyBusinessEntities } from 'views/backoffice/BusinessEntities/types';
import { DynamicObject } from 'views/TenantProfileSettings/components/types';
import useMyMenuItems from 'hooks/useMyMenuItems';
import { QUERY_FIND_MANY_BUSINESS_ENTITIES } from 'graphql/queries/businessEntities';
import { useQuery } from '@apollo/client';
import { ITenant } from 'types/tenant';
import { PanelLoader } from 'ui-component/RecordView/components/PanelLoader';

interface PopoverBaseProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    origin: PopoverOrigin;
    children: React.ReactNode;
    handleOpenEntitiesModule: () => void;
    disableOpenEntitiesModule: boolean;
}

interface EntityPopoverProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    entityId?: number | string;
    isDropdown?: boolean;
}

const formatCurrency = (value: number | string): string => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '0,00';

    return numValue
        ? numValue.toLocaleString('de-DE', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
          })
        : '0,00';
};

const formatContact = (contact: DynamicObject | null) => {
    if (!contact) return 'N/A';
    const firstName = contact?.objectValues.find((value) => value.objectProperty?.name === 'First Name')?.value || '';
    const lastName = contact?.objectValues.find((value) => value.objectProperty?.name === 'Last Name')?.value || '';
    const role = contact?.objectValues.find((value) => value.objectProperty?.name === 'Role')?.value || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return role ? `${fullName}, ${role}` : fullName;
};

const getAssociatedTenants = (associatedTenants: ITenant[]) =>
    associatedTenants && associatedTenants.length > 0 ? associatedTenants.map((tenant) => tenant.name).join(', ') : 'N/A';

const PopoverBase = ({
    open,
    anchorEl,
    onClose,
    origin,
    children,
    handleOpenEntitiesModule,
    disableOpenEntitiesModule
}: PopoverBaseProps) => {
    const theme = useTheme();
    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={origin}
            transformOrigin={origin}
            sx={{
                '& .MuiPopover-paper': {
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    width: '25rem',
                    wordWrap: 'break-word',
                    boxShadow: '0 0.25rem 1.25rem rgba(0, 0, 0, 0.15)',
                    border: '0.0625rem solid #e0e0e0',
                    marginTop: '0.5rem'
                }
            }}
        >
            <Box onMouseLeave={onClose} width="100%">
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: '0.5rem',
                        borderBottom: '0.0625rem solid #f0f0f0',
                        backgroundColor: theme.palette.primary[300]
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            fontSize: '1rem'
                        }}
                    >
                        Metafields
                    </Typography>
                    <Button
                        sx={{
                            px: '0.5rem',
                            py: '0.25rem',
                            borderRadius: '0.5rem',
                            borderColor: theme.palette.secondary.dark,
                            color: theme.palette.secondary.dark,
                            backgroundColor: theme.palette.background.paper
                        }}
                        variant="outlined"
                        disabled={disableOpenEntitiesModule}
                        startIcon={<OpenInBrowserOutlined />}
                        onClick={handleOpenEntitiesModule}
                    >
                        Open Entities Module
                    </Button>
                </Box>
                {children}
            </Box>
        </Popover>
    );
};

export const EntityPopover = ({ open, anchorEl, onClose, entityId, isDropdown }: EntityPopoverProps) => {
    const { menuItemList } = useMyMenuItems();

    const { data, loading } = useQuery<FindManyBusinessEntities>(QUERY_FIND_MANY_BUSINESS_ENTITIES, {
        variables: {
            data: { id: Number(entityId) }
        },
        skip: !entityId
    });

    if (!entityId) return null;

    const origin: PopoverOrigin = {
        vertical: isDropdown ? 'bottom' : 'top',
        horizontal: isDropdown ? 'left' : 'center'
    };

    const entityData = data?.findManyBusinessEntities?.[0];

    const handleOpenEntitiesModule = () => {
        if (menuItemList) {
            const entitiesMenuItem = menuItemList.find((item) => item.name === 'Entities');
            if (entitiesMenuItem) {
                const tenantUrl = localStorage.getItem('tenantPath');
                if (!tenantUrl) return;
                window.open(`${tenantUrl}/${entitiesMenuItem.fullPath}?selected=${entityId}`, '_blank');
            }
        }
    };

    if (loading || !entityData)
        return (
            <PopoverBase
                open={open}
                anchorEl={anchorEl}
                onClose={onClose}
                origin={origin}
                handleOpenEntitiesModule={handleOpenEntitiesModule}
                disableOpenEntitiesModule
            >
                <PanelLoader maxItems={5} />
            </PopoverBase>
        );

    const { id, displayName, associatedTenants, einNumber, taxIdentificationNum, apContact, arContact, freightThreshold } = entityData;

    const fieldsValues = [
        {
            label: 'Associated Tenant:',
            value: getAssociatedTenants(associatedTenants as ITenant[])
        },
        {
            label: 'Tenant Display Name:',
            value: displayName || 'N/A'
        },
        {
            label: 'EIN:',
            value: einNumber || 'N/A'
        },
        {
            label: 'Tax ID:',
            value: taxIdentificationNum || 'N/A'
        },
        {
            label: 'AP Contact:',
            value: formatContact(apContact as DynamicObject)
        },
        {
            label: 'AR Contact:',
            value: formatContact(arContact as DynamicObject)
        },
        {
            label: 'Freight Threshold:',
            value: formatCurrency(freightThreshold),
            unit: 'USD'
        }
    ];

    return (
        <PopoverBase
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            origin={origin}
            handleOpenEntitiesModule={handleOpenEntitiesModule}
            disableOpenEntitiesModule={!id}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: '0.5rem' }}>
                {fieldsValues.map((field) => (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 300, fontSize: '0.875rem' }}>{field.label}</Typography>
                        <Typography sx={{ fontWeight: 500, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {field.unit && (
                                <Typography
                                    sx={{
                                        fontWeight: 500,
                                        fontSize: '0.625rem',
                                        lineHeight: 1,
                                        transform: 'translateY(-0.1875rem)'
                                    }}
                                >
                                    {field.unit}
                                </Typography>
                            )}
                            {field.value}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </PopoverBase>
    );
};
